import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { t } from '../i18n/index.js'
import { addDays, formatDate, formatWeekday, relativeDayLabel } from '../lib/format.js'
import { PropertyGroup, groupByProperty } from './PropertyGroup.js'
import { TaskSkeleton } from './TaskCard.js'
import { WeekStrip } from './WeekStrip.js'
import { WorkerShell, useWorkerTasks } from './shell.js'

/**
 * The fortnight ahead, grouped by day.
 *
 * Workers plan their own week, which day is heavy, when they can take time off,
 * whether Saturday is free. Showing only today forces them to ask the office.
 */
export function PlanPage() {
  const [params, setParams] = useSearchParams()
  const { tasks, today, byDay, to, isLoading } = useWorkerTasks()
  const selected = params.get('tag')
  const dayRefs = useRef(new Map<string, HTMLDivElement>())

  const days: string[] = []
  for (let date = today; date <= to; date = addDays(date, 1)) days.push(date)

  // Coming from the week strip on the Today screen, jump straight to that day.
  useEffect(() => {
    if (!selected) return
    const node = dayRefs.current.get(selected)
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [selected, isLoading])

  const upcoming = tasks.filter((task) => task.dueDate >= today)

  return (
    <WorkerShell title={t('plan.title')} subtitle={t('plan.subtitle')}>
      <WeekStrip
        today={today}
        byDay={byDay}
        loading={isLoading && tasks.length === 0}
        selected={selected ?? undefined}
        onSelect={(date) => setParams({ tag: date }, { replace: true })}
      />

      {isLoading && tasks.length === 0 && <TaskSkeleton count={3} />}

      {!isLoading && upcoming.length === 0 && (
        <p className="muted" style={{ fontSize: 17 }}>
          {t('plan.nothing')}
        </p>
      )}

      {days.map((date) => {
        const dayTasks = byDay.get(date) ?? []
        // Empty future days are hidden unless explicitly selected, otherwise the
        // list is mostly whitespace.
        if (dayTasks.length === 0 && date !== selected) return null

        const open = dayTasks.filter((task) => task.status !== 'done').length

        return (
          <div
            key={date}
            className={`wk-daygroup ${date === today ? 'is-today' : ''}`}
            ref={(node) => {
              if (node) dayRefs.current.set(date, node)
              else dayRefs.current.delete(date)
            }}
          >
            <h2>
              <span>
                {relativeDayLabel(date, today)}
                <span className="muted" style={{ fontWeight: 500 }}>
                  {' '}
                  ({formatWeekday(date)}, {formatDate(date)})
                </span>
              </span>
              <span className="n">
                {dayTasks.length === 0
                  ? '–'
                  : open === 0
                    ? t('plan.all_done')
                    : t('plan.open_count', { count: open })}
              </span>
            </h2>

            {dayTasks.length === 0 ? (
              <div className="wk-empty-day">{t('plan.nothing_day')}</div>
            ) : (
              groupByProperty(dayTasks).map((group) => (
                <PropertyGroup key={group.key} group={group} today={today} />
              ))
            )}
          </div>
        )
      })}
    </WorkerShell>
  )
}
